/**
 * Leads API functions + TanStack Query hooks.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { api } from '@core/api/client';
import { LEADS_LIST_PAGE_SIZE } from '../config/ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Lead {
  id: string;
  tenant_id: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  phone_normalized?: string | null;
  source: string;
  stage: string;
  previous_stage?: string | null;
  interests: string[];
  preferred_branch_id?: string | null;
  assigned_to?: string | null;
  notes?: string | null;
  follow_up_date?: string | null;
  trial_booking_id?: string | null;
  trial_date?: string | null;
  trial_feedback?: string | null;
  converted_to_student_id?: string | null;
  converted_at?: string | null;
  converted_by?: string | null;
  lost_reason?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  classcard_id?: string | null;
  last_contacted?: string | null;
  interest_level: string;
  interest_level_computed: string;
  interest_level_computed_score: number;
  interest_level_computed_reasons: Array<{ code: string; weight: number; detail?: string | null }>;
  interest_level_computed_at?: string | null;
  interest_level_manual_override?: string | null;
  interest_level_manual_override_by?: string | null;
  interest_level_manual_override_at?: string | null;
  interest_level_manual_override_expires_at?: string | null;
  interest_level_manual_override_reason?: string | null;
}

export interface LeadCreate {
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  source: string;
  stage?: string;
  interests?: string[];
  preferred_branch_id?: string | null;
  assigned_to?: string | null;
  notes?: string | null;
  follow_up_date?: string | null;
  last_contacted?: string | null;
}

export interface LeadUpdate {
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string | null;
  source?: string;
  stage?: string;
  interests?: string[];
  preferred_branch_id?: string | null;
  assigned_to?: string | null;
  notes?: string | null;
  follow_up_date?: string | null;
  last_contacted?: string | null;
  lost_reason?: string | null;
}

export interface LeadNote {
  id: string;
  type: string;
  lead_id: string;
  actor_id: string;
  note?: string | null;
  created_at: string;
}

export interface PaginatedLeads {
  items: Lead[];
  total: number;
  next_cursor?: string | null;
}

export interface BulkParseResult {
  rows: Array<{
    row_index: number;
    first_name: string;
    last_name?: string;
    phone?: string;
    email?: string;
    source?: string;
    interests?: string[];
    notes?: string;
    errors: string[];
    is_valid: boolean;
    is_duplicate_warning: boolean;
    duplicate_leads?: Lead[];
  }>;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  duplicate_warning_rows: number;
}

export interface BulkCommitResult {
  total: number;
  created: number;
  failed: number;
  results: Array<{
    row_index: number;
    status: 'created' | 'failed';
    lead_id?: string;
    error?: string;
  }>;
}

export interface ConvertLeadPayload {
  date_of_birth?: string | null;
  gender?: string | null;
  guardian_first_name?: string | null;
  guardian_last_name?: string | null;
  guardian_phone?: string | null;
  guardian_email?: string | null;
  guardian_relationship?: string | null;
  notes?: string | null;
}

export interface ConvertLeadResult {
  student_id: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const leadKeys = {
  all: ['leads'] as const,
  list: (params: object) => ['leads', 'list', params] as const,
  detail: (id: string) => ['leads', id] as const,
  notes: (id: string) => ['leads', id, 'notes'] as const,
  activities: (id: string) => ['leads', id, 'activities'] as const,
  duplicates: (id: string) => ['leads', id, 'duplicates'] as const,
};

// ---------------------------------------------------------------------------
// API params
// ---------------------------------------------------------------------------

export interface ListLeadsParams {
  stage?: string;
  assigned_to?: string;
  search?: string;
  follow_up_before?: string;
  interest_level?: string;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useLeads(params: ListLeadsParams) {
  return useInfiniteQuery<
    PaginatedLeads,
    Error,
    InfiniteData<PaginatedLeads>,
    ReturnType<typeof leadKeys.list>,
    string | undefined
  >({
    queryKey: leadKeys.list(params),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedLeads>('/v1/leads', {
        query: {
          ...params,
          cursor: pageParam,
          limit: LEADS_LIST_PAGE_SIZE,
        },
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

export function useLead(id: string) {
  return useQuery({
    queryKey: leadKeys.detail(id),
    queryFn: () => api.get<Lead>(`/v1/leads/${id}`),
    enabled: Boolean(id),
  });
}

export function useLeadNotes(leadId: string) {
  return useQuery({
    queryKey: leadKeys.notes(leadId),
    queryFn: () => api.get<LeadNote[]>(`/v1/leads/${leadId}/notes`),
    enabled: Boolean(leadId),
  });
}

export function useLeadActivities(leadId: string) {
  return useQuery({
    queryKey: leadKeys.activities(leadId),
    queryFn: () => api.get<LeadNote[]>(`/v1/leads/${leadId}/activities`),
    enabled: Boolean(leadId),
  });
}

export function useLeadDuplicates(leadId: string, enabled = true) {
  return useQuery({
    queryKey: leadKeys.duplicates(leadId),
    queryFn: () => api.get<Lead[]>(`/v1/leads/${leadId}/duplicates`),
    enabled: Boolean(leadId) && enabled,
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeadCreate) => api.post<Lead>('/v1/leads', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LeadUpdate }) =>
      api.patch<Lead>(`/v1/leads/${id}`, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: leadKeys.all });
      qc.invalidateQueries({ queryKey: leadKeys.detail(id) });
    },
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/leads/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, note }: { leadId: string; note: string }) =>
      api.post<LeadNote>(`/v1/leads/${leadId}/notes`, { note }),
    onSuccess: (_data, { leadId }) => {
      qc.invalidateQueries({ queryKey: leadKeys.notes(leadId) });
    },
  });
}

export function useBulkParse() {
  return useMutation({
    mutationFn: (payload: { raw_text: string; source?: string }) =>
      api.post<BulkParseResult>('/v1/leads/bulk/parse', payload),
  });
}

export function useBulkCommit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { rows: object[]; source?: string }) =>
      api.post<BulkCommitResult>('/v1/leads/bulk/commit', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

export function useBulkReassign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { lead_ids: string[]; assigned_to: string }) =>
      api.post<{ updated: number }>('/v1/leads/bulk/reassign', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

export function useRecomputeInterest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<Lead>(`/v1/leads/${id}/recompute-interest`, {}),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: leadKeys.detail(id) });
    },
  });
}

export function useOverrideInterest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: {
        interest_level: string;
        reason: string;
        expires_at?: string | null;
      };
    }) => api.post<Lead>(`/v1/leads/${id}/override-interest`, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: leadKeys.detail(id) });
    },
  });
}

export function useConvertLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ConvertLeadPayload }) =>
      api.post<ConvertLeadResult>(`/v1/leads/${id}/convert`, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: leadKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ['students'] });
    },
  });
}

/** Returns a URL string for CSV export with optional query params. */
export function getExportCsvUrl(params: Record<string, string>): string {
  const qs = new URLSearchParams(params).toString();
  return `/v1/leads/export/csv${qs ? `?${qs}` : ''}`;
}
